import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import './styles.css';  // Import global CSS
import API_URL from './config.js';
import LoginView from './views/LoginView';
import { getToken, setToken as setAuthToken, applyAxiosAuth, authFetch, clearToken, getStoredUser, setStoredUser } from './auth';

// css
import './AppLayout.css';
import './components/ButtonStyles.css';

// UI Components
import TopHeader from './components/TopHeader';
import CabinetsModal from './components/CabinetsModal';
import SidebarMenu from './components/SidebarMenu';

// views
import UploadView from './views/UploadView';
import SearchView from './views/SearchView';
import ManageView from './views/ManageView';
import AllFilesView from './views/AllFilesView';
import UsersView from './views/UsersView';
import AdminStatusView from './views/AdminStatusView';
import AdminCabinetsView from './views/AdminCabinetsView';
import EmailModal from './components/EmailModal';
import NewBarcodeView from './views/NewBarcodeView';

function App() {
  const [customerName, setCustomerName] = useState(() => {
    try { return localStorage.getItem('customerName') || ''; } catch (_) { return ''; }
  });
  const [cabinetFields, setCabinetFields] = useState([]);
  const [fieldKeys, setFieldKeys] = useState([]);
    const [file, setFile] = useState(null);
    const [files, setFiles] = useState([]);
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [view, setView] = useState(() => {
      try { return localStorage.getItem('app.view') || 'upload'; } catch (_) { return 'upload'; }
    });
    // Sort state for file listings (persisted in localStorage for All Files view)
    const [sortField, setSortField] = useState(() => {
      try { return localStorage.getItem('allFiles.sortField') || null; } catch (_) { return null; }
    });
    const [sortDirection, setSortDirection] = useState(() => {
      try { return localStorage.getItem('allFiles.sortDirection') || 'asc'; } catch (_) { return 'asc'; }
    });
    const [selectedRow, setSelectedRow] = useState(null);  // Track selected row
    const [searching, setSearching] = useState(false);
    const [searchCancelToken, setSearchCancelToken] = useState(null);
    const [searchSortField, setSearchSortField] = useState(null);
  const [searchSortDirection, setSearchSortDirection] = useState('asc');
  const [ocrStatus, setOcrStatus] = useState(null);
  const [auth, setAuth] = useState({ token: '', user: null });
  // UI theme reflects the immediate, effective theme (optimistic toggle)
  // Initialize from localStorage to persist across refreshes before user data loads
  const [uiTheme, setUiTheme] = useState(() => {
    try {
      return (localStorage.getItem('theme') || 'light').toLowerCase();
    } catch (_) {
      return 'light';
    }
  });
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailFilename, setEmailFilename] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailBusyText, setEmailBusyText] = useState('Sending…');
  const [cabinetsOpen, setCabinetsOpen] = useState(false);



  // Progress and upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [cancelToken, setCancelToken] = useState(null);
  const [uploadName, setUploadName] = useState('');
  const [uploadIndex, setUploadIndex] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [uploadOverallProgress, setUploadOverallProgress] = useState(0);
  const [uploadEta, setUploadEta] = useState('');

  // Toast notifications
  const [toast, setToast] = useState({ open: false, message: '', type: 'info' });
  const toastRef = useRef(toast);
  useEffect(() => { toastRef.current = toast; }, [toast]);
  const showToast = (message, type = 'success', durationMs = 3000) => {
    const current = toastRef.current || {};
    // Dedupe identical, already-open toasts (prevents spam like repeated "Loading files…")
    if (current.open && current.message === message && current.type === type) {
      // If a finite duration is requested, refresh its timeout but do not re-render
      window.clearTimeout(showToast._t);
      if (durationMs > 0) {
        showToast._t = window.setTimeout(() => setToast(t => ({ ...t, open: false })), durationMs);
      }
      return;
    }
    setToast({ open: true, message, type });
    window.clearTimeout(showToast._t);
    if (durationMs > 0) {
      showToast._t = window.setTimeout(() => setToast(t => ({ ...t, open: false })), durationMs);
    }
  };
  // Expose toast for children that don't receive props
  useEffect(() => {
    try { window.spartaToast = showToast; } catch (_) {}
    return () => { try { delete window.spartaToast; } catch (_) {} };
  }, []);

  // Expose a global setter so admin pages can update the header customer name immediately (e.g., after rename)
  useEffect(() => {
    try { window.spartaSetCustomerName = (name) => setCustomerName(String(name || '')); } catch (_) {}
    return () => { try { delete window.spartaSetCustomerName; } catch (_) {} };
  }, []);

  // Greet user once per browser session (persist flag in sessionStorage) with a small delay
  let _initialGreeted = false;
  try { _initialGreeted = sessionStorage.getItem('sparta.greeted') === '1'; } catch (_) { /* ignore */ }
  const greetedRef = useRef(_initialGreeted);
  const greetTimerRef = useRef(null);
  const scheduleGreeting = () => {
    if (!auth?.user || greetedRef.current) return;
    window.clearTimeout(greetTimerRef.current);
    greetTimerRef.current = window.setTimeout(() => {
      try {
        if (greetedRef.current) return;
        const hour = new Date().getHours();
        const period = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
        const uname = (auth?.user && (`${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim() || auth.user.login)) || 'User';
        showToast(`Good ${period}, ${uname}`, 'info', 3000);
        greetedRef.current = true;
        try { sessionStorage.setItem('sparta.greeted', '1'); } catch (_) {}
      } catch (_) {}
    }, 1500); // allow any startup/progress toasts to show first
  };
  useEffect(() => { scheduleGreeting(); return () => window.clearTimeout(greetTimerRef.current); }, [auth?.user]);

  // --- Session timeout and auto-logout ---
  const idleTimerRef = useRef(null);
  const expTimerRef = useRef(null);
  const activityBoundRef = useRef(false);
  const INACTIVITY_MS = 30 * 60 * 1000; // 30 minutes

  function decodeJwtExp(token) {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const payload = parts[1]
        .replace(/-/g, '+')
        .replace(/_/g, '/');
      const json = JSON.parse(decodeURIComponent(atob(payload).split('').map(c =>
        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      ).join('')));
      if (json && typeof json.exp === 'number') return json.exp * 1000; // ms
      return null;
    } catch (_) {
      return null;
    }
  }

  useEffect(() => {
    // Cleanup helper
    const clearTimers = () => {
      if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
      if (expTimerRef.current) { clearTimeout(expTimerRef.current); expTimerRef.current = null; }
    };
    const unbindActivity = () => {
      if (!activityBoundRef.current) return;
      activityBoundRef.current = false;
      const evs = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
      evs.forEach(ev => window.removeEventListener(ev, resetIdle));
    };

    const doLogout = (reason) => {
      try { clearToken(); } catch(_) {}
      setAuth({ token: '', user: null });
      clearTimers();
      unbindActivity();
      if (reason === 'expired') {
        showToast('Session expired. Please sign in again.', 'info', 5000);
      } else if (reason === 'idle') {
        showToast('Signed out after 30 minutes of inactivity.', 'info', 5000);
      }
    };

    const resetIdle = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => doLogout('idle'), INACTIVITY_MS);
    };

    clearTimers();
    unbindActivity();

    if (!auth.token) return; // nothing to schedule when logged out

    // Bind user activity to reset idle timer
    const evs = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    evs.forEach(ev => window.addEventListener(ev, resetIdle));
    activityBoundRef.current = true;
    resetIdle(); // start idle countdown

    // Schedule logout just before token expiration (if known)
    const expMs = decodeJwtExp(auth.token);
    if (typeof expMs === 'number' && expMs > Date.now()) {
      const msUntil = Math.max(1000, expMs - Date.now() - 5000); // 5s safety margin
      expTimerRef.current = setTimeout(() => doLogout('expired'), msUntil);
    }

    // Cleanup when token changes or component unmounts
    return () => {
      clearTimers();
      unbindActivity();
    };
  }, [auth.token]);



  // Handle single or multiple file upload
  const handleUpload = async () => {
    // Build a sequential list of files to upload
    const filesToUpload = [];
    if (file) filesToUpload.push(file);
    if (files && files.length) filesToUpload.push(...files);

    if (!filesToUpload.length) {
      alert('Please choose at least one PDF to upload.');
      return;
    }

    setUploadTotal(filesToUpload.length);
    setUploadIndex(0);
    setUploading(true);
    setUploadOverallProgress(0);
    setUploadEta('');
    try { showToast(`Uploading ${filesToUpload.length} file${filesToUpload.length === 1 ? '' : 's'}…`, 'info', 0); } catch (_) {}

    // Pre-compute total bytes for ETA estimation
    const totalBytes = filesToUpload.reduce((sum, f) => sum + (f?.size || 0), 0);
    let bytesDone = 0; // sum of fully uploaded prior files
    const startTs = Date.now();

    let successCount = 0;
    const uploadedNames = [];
    let aborted = false;
    const API_URL = import.meta.env.VITE_API_URL;

    for (let i = 0; i < filesToUpload.length; i++) {
      const f = filesToUpload[i];
      setUploadIndex(i + 1);
      setUploadName(f.name);
      setUploadProgress(0);

      const formData = new FormData();
      // Send as single file field supported by backend
      formData.append('pdf', f);

      const source = axios.CancelToken.source();
      setCancelToken(source);

      try {
        await axios.post(`${API_URL}/upload`, formData, {
          onUploadProgress: (progressEvent) => {
            const loaded = Math.max(0, progressEvent.loaded || 0);
            const totalForEvent = Math.max(1, progressEvent.total || f.size || 1);
            const percent = Math.min(100, Math.round((loaded * 100) / totalForEvent));
            setUploadProgress(percent);
            // Overall progress combines completed files + fraction of current
            const fraction = percent / 100;
          const overall = uploadTotal > 0 ? Math.round(((i + fraction) / uploadTotal) * 100) : percent;
          setUploadOverallProgress(overall);
            try { showToast(`Uploading… ${overall}%`, 'info', 0); } catch (_) {}

            // ETA: estimate based on total bytes and elapsed throughput
            if (totalBytes > 0) {
              const uploadedSoFar = bytesDone + loaded;
              const elapsedSec = Math.max(0.001, (Date.now() - startTs) / 1000);
              const bytesPerSec = uploadedSoFar / elapsedSec;
              if (bytesPerSec > 0) {
                const remainingBytes = Math.max(0, totalBytes - uploadedSoFar);
                const etaSec = Math.round(remainingBytes / bytesPerSec);
                const mins = Math.floor(etaSec / 60);
                const secs = etaSec % 60;
                const etaStr = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
                setUploadEta(etaStr);
              }
            }
          },
          cancelToken: source.token,
        });
        successCount += 1;
        try { uploadedNames.push(f?.name || ''); } catch (_) {}
        // Snap overall to the boundary for completed file
        if (uploadTotal) {
          const overall = Math.round(((i + 1) / uploadTotal) * 100);
          setUploadOverallProgress(overall);
        }
        // Advance bytesDone for ETA base
        bytesDone += f?.size || 0;
      } catch (err) {
        if (axios.isCancel(err)) {
          console.log('Upload cancelled');
          aborted = true;
          break;
        } else {
          console.error('Upload failed for', f?.name, err);
          // Stop on first hard failure to keep feedback simple
          aborted = true;
          break;
        }
      } finally {
        setCancelToken(null);
      }
    }

    if (!aborted && successCount === filesToUpload.length) {
      setFiles([]);
      setFile(null);
      const msg = filesToUpload.length === 1
        ? `Uploaded ${filesToUpload[0].name}`
        : `Uploaded ${successCount} files`;
      showToast(msg, 'success');
      // Offer to download the uploaded PDF if only one file
      try {
        if (uploadedNames.length === 1 && uploadedNames[0]) {
          const want = window.confirm('Download the uploaded PDF to your computer now?');
          if (want) {
            const API_URL = import.meta.env.VITE_API_URL;
            const res = await authFetch(`${API_URL}/files/${encodeURIComponent(uploadedNames[0])}`);
            if (res && res.ok) {
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = uploadedNames[0];
              document.body.appendChild(a);
              a.click();
              a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 5000);
            }
          }
        }
      } catch (_) { /* ignore download failures */ }
    } else if (aborted) {
      showToast('Upload cancelled or failed.', 'error', 3500);
    }

    // Reset UI state
    setUploading(false);
    setUploadProgress(0);
    setUploadName('');
    setUploadIndex(0);
    setUploadTotal(0);
    setUploadOverallProgress(0);
    setUploadEta('');
  }; // handleUpload



  // Cancel the upload if needed
  const cancelUpload = () => {
    if (cancelToken) {
      cancelToken.cancel('Upload cancelled by user');
    }
  };



  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'application/pdf': ['.pdf'] },
    onDrop: (acceptedFiles) => setFiles(acceptedFiles),
    multiple: true, // Allow multiple files
  });



  const handleSearch = async () => {
    const terms = query.split('+').map(term => term.trim().toLowerCase()).filter(Boolean);

    if (terms.length === 0) {
        alert('Please enter some search terms.');
        return;
    }

    const source = axios.CancelToken.source();
    setSearchCancelToken(source); // store it
    setSearching(true);

    try {
        try { showToast('Searching…', 'info', 0); } catch (_) {}
        const res = await axios.get(`${API_URL}/search?q=` + terms.join(' '), {
        cancelToken: source.token
        });
        const arr = Array.isArray(res.data) ? res.data : [];
        setResults(arr);
        setHasSearched(true);
        try { showToast(`Found ${arr.length} result${arr.length === 1 ? '' : 's'}`, 'success', 2000); } catch (_) {}
    } catch (err) {
        if (axios.isCancel(err)) {
        console.log('Search cancelled');
        try { showToast('Search cancelled', 'info', 2000); } catch (_) {}
        } else {
        console.error('Search failed:', err);
        try { showToast('Search failed', 'error', 3500); } catch (_) {}
        }
    } finally {
        setSearching(false);
        setSearchCancelToken(null);
    }
}; // handlesearch

  // Clear the search input and results
  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
  };



const handleSearchSort = (field) => {
  const direction =
    searchSortField === field && searchSortDirection === 'asc'
      ? 'desc'
      : 'asc';

  setSearchSortField(field);
  setSearchSortDirection(direction);
};


  const loadingFilesRef = useRef(false);
  const loadFiles = async (opts = {}) => {
    const silent = !!opts.silent;
    if (loadingFilesRef.current) {
      // Avoid overlapping loads and duplicate toasts
      return;
    }
    loadingFilesRef.current = true;
    try {
      const API_URL = import.meta.env.VITE_API_URL;
      if (!silent) { try { showToast('Loading files…', 'info', 0); } catch (_) {} }
      const res = await axios.get(`${API_URL}/files`);
      const list = Array.isArray(res.data) ? res.data : [];
      setFileList(list);
      if (!silent) {
        try {
          const repaired = parseInt((res.headers && (res.headers['x-repair-writes'] || res.headers['X-Repair-Writes'])) || '0', 10) || 0;
          if (repaired > 0) {
            showToast(`Repaired ${repaired} file${repaired === 1 ? '' : 's'} this pass`, 'info', 2000);
          } else {
            showToast(`Loaded ${list.length} file${list.length === 1 ? '' : 's'}`, 'success', 1200);
          }
        } catch (_) {}
      }
    } catch (err) {
      console.error('Failed to load files:', err);
      if (!silent) { try { showToast('Failed to load files', 'error', 3500); } catch (_) {} }
    } finally {
      loadingFilesRef.current = false;
    }
  };

  const handleDelete = async (filename) => {
    try {
      const API_URL = import.meta.env.VITE_API_URL;
      await axios.delete(`${API_URL}/files/` + filename);
      setFileList(prev => prev.filter(f => f.filename !== filename));
      showToast(`Deleted: ${filename}`, 'success', 2500);
    } catch (err) {
      console.error('Failed to delete file:', err);
      showToast('Failed to delete file', 'error', 3500);
    }
  };

  const parseFilename = (filename) => {
    const base = filename.toLowerCase().endsWith('.pdf') ? filename.slice(0, -4) : filename;
    const parts = base.split('_');
    const obj = {};
    for (let i = 0; i < fieldKeys.length; i++) {
      obj[fieldKeys[i]] = parts[i] || '';
    }
    return obj;
  };

  const handleSort = (field) => {
    const direction = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(direction);
  };

  // Persist sort selection for All Files table
  useEffect(() => {
    try {
      if (sortField) localStorage.setItem('allFiles.sortField', sortField);
      if (sortDirection) localStorage.setItem('allFiles.sortDirection', sortDirection);
    } catch (_) {}
  }, [sortField, sortDirection]);

  // Persist the current view so refresh restores it
  useEffect(() => {
    try { localStorage.setItem('app.view', view); } catch (_) {}
  }, [view]);

  // Hide deprecated/duplicate 'manage' view by redirecting to 'all'
  useEffect(() => {
    if (view === 'manage') {
      setView('all');
    }
  }, [view]);

  // When entering the All Files view, hydrate sort state from localStorage
  useEffect(() => {
    if (view !== 'all') return;
    try {
      const sf = localStorage.getItem('allFiles.sortField');
      const sd = localStorage.getItem('allFiles.sortDirection');
      if (sf) setSortField(sf);
      if (sd === 'asc' || sd === 'desc') setSortDirection(sd);
    } catch (_) {}
  }, [view]);

  const getSortedFiles = () => {
    const sorted = [...fileList];
    const sf = sortField && fieldKeys.includes(sortField) ? sortField : (fieldKeys[0] || null);
    if (!sf) return sorted;

    sorted.sort((a, b) => {
      const aVal = String(parseFilename(a.filename)[sf] || '').toLowerCase();
      const bVal = String(parseFilename(b.filename)[sf] || '').toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  useEffect(() => {
    if (!auth.token) return;
    if (view === 'manage' || view === 'all') {
      loadFiles();
    }
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/cabinets/active`);
        if (res.ok) {
          const active = await res.json();
          if (active && active.name) {
            setCustomerName(active.name);
            try { localStorage.setItem('customerName', String(active.name)); } catch (_) {}
          }
          const raw = String(active?.fields || '').trim();
          const labels = (raw ? raw.split(';') : []).map(s => s.trim()).filter(Boolean);
          const usedLabels = labels.length ? labels : ['File #', 'Buyer Name', 'Seller Name'];
          const keys = usedLabels.map(l => l.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''));
          setCabinetFields(usedLabels);
          setFieldKeys(keys);
          setSortField(prev => (keys.includes(prev) ? prev : (keys[0] || null)));
        }
      } catch (e) {
        // ignore
      }
    })();
  }, [view, auth.token]);

  // Poll OCR status to drive subtle header progress
  useEffect(() => {
    if (!auth.token) return; // no polling when logged out
    let timer;
    const API_URL = import.meta.env.VITE_API_URL;
    const prev = { active: false, processed: 0 };
    const poll = async () => {
      try {
        const pauseUI = typeof window !== 'undefined' && (window.spartaPauseUIUpdates === true);
        if (pauseUI) return; // skip state updates that can collapse open dropdowns
        const res = await authFetch(`${API_URL}/ocr/status`);
        const json = await res.json();
        // Skip setting state while Admin Status editor is active to avoid re-renders
        if (!(typeof window !== 'undefined' && window.spartaPauseUIUpdates === true)) {
          setOcrStatus(json);
        }
        // If OCR just processed a file or finished, refresh file list in views
        const processedChanged = typeof json?.processed === 'number' && json.processed !== prev.processed;
        const finishedNow = prev.active && !json?.active;
        if (!pauseUI && (processedChanged || finishedNow) && (view === 'manage' || view === 'all')) {
          try { await loadFiles({ silent: true }); } catch (_) {}
        }
        // Avoid disruptive toasts while editing settings on Admin Status
        const pauseToasts = typeof window !== 'undefined' && window.spartaPauseToasts === true;
        if (!pauseToasts) {
          prev.active = !!json?.active;
          prev.processed = Number(json?.processed || 0);
        } else {
          // Still track state but don't emit toasts; just update counters silently
          prev.active = !!json?.active;
          prev.processed = Number(json?.processed || 0);
        }
      } catch (e) {
        // ignore polling errors; keep last known status
      }
    };
    poll();
    timer = setInterval(poll, 1500);
    return () => clearInterval(timer);
  }, [auth.token, view]);

  // Poll Repair status and surface toasts
  useEffect(() => {
    if (!auth.token) return;
    const API_URL = import.meta.env.VITE_API_URL;
    let timer;
    const prev = { active: false, processed: 0 };
    const poll = async () => {
      try {
        const res = await authFetch(`${API_URL}/repair/status`);
        const json = await res.json();
        const active = !!json?.active;
        const processed = Number(json?.processed || 0);
        const total = Number(json?.total || 0);
        const pauseToasts = typeof window !== 'undefined' && window.spartaPauseToasts === true;
        if (!pauseToasts) {
          if (active && !prev.active) {
            try { showToast(`Repairing${total ? ` ${Math.min(processed+1,total)}/${total}` : ''}…`, 'info', 0); } catch (_) {}
          } else if (active && (processed !== prev.processed)) {
            try { showToast(`Repairing ${Math.min(processed+1,total)}/${total}`, 'info', 0); } catch (_) {}
          } else if (!active && prev.active) {
            try { showToast('Repair complete', 'success', 2500); } catch (_) {}
          }
        }
        prev.active = active;
        prev.processed = processed;
      } catch (e) {
        // ignore polling errors
      }
    };
    poll();
    timer = setInterval(poll, 1500);
    return () => clearInterval(timer);
  }, [auth.token, view]);

  // Initialize auth from localStorage
  useEffect(() => {
    const token = getToken();
    if (token) {
      applyAxiosAuth(token);
      const cachedUser = getStoredUser();
      setAuth({ token, user: cachedUser });
      // Hydrate user profile (for theme, name, etc.)
      (async () => {
        try {
          const res = await authFetch(`${API_URL}/auth/me`);
          if (res.ok) {
            const user = await res.json();
            setAuth(a => ({ ...a, user }));
            setStoredUser(user);
          }
        } catch (_) {}
      })();
    }
  }, []);

  const handleLoginSuccess = (token, user) => {
    setAuthToken(token);
    applyAxiosAuth(token);
    setAuth({ token, user });
    setStoredUser(user);
    // Schedule greeting after login
    try { greetedRef.current = false; scheduleGreeting(); } catch (_) {}
  };

  // Apply theme to document whenever effective theme changes, and persist to localStorage
  useEffect(() => {
    const theme = (uiTheme || 'light').toLowerCase();
    document.documentElement.setAttribute('data-theme', theme === 'dark' ? 'dark' : 'light');
    try { localStorage.setItem('theme', theme); } catch (_) {}
  }, [uiTheme]);

  // Update browser tab title and persist cabinet name when it changes
  useEffect(() => {
    const name = (customerName || '').trim();
    try { localStorage.setItem('customerName', name); } catch (_) {}
    document.title = name || (import.meta.env.VITE_COMPANY_NAME || 'TrueView Docs');
  }, [customerName]);

  // Keep uiTheme in sync with user profile theme when it changes (e.g., after login or server update)
  // Only apply if the server actually provides a theme; don't overwrite local choice with a default.
  useEffect(() => {
    if (!auth?.user?.theme) return;
    const theme = String(auth.user.theme).toLowerCase();
    setUiTheme(theme);
    try { localStorage.setItem('theme', theme); } catch (_) {}
  }, [auth?.user?.theme]);

  // Global handler for unauthorized responses -> force logout and notify
  useEffect(() => {
    const onUnauthorized = () => {
      try { clearToken(); } catch(_) {}
      setAuth({ token: '', user: null });
      showToast('Session expired. Please sign in again.', 'info', 5000);
    };
    window.addEventListener('sparta:unauthorized', onUnauthorized);
    return () => window.removeEventListener('sparta:unauthorized', onUnauthorized);
  }, []);

  // render
  const renderContent = () => {
    if (view === 'upload') {
      return (
        <UploadView
            file={file}
            setFile={setFile}
            files={files}
            setFiles={setFiles}
            handleUpload={handleUpload}
            uploading={uploading}
            cancelUpload={cancelUpload}
            uploadProgress={uploadProgress}
            uploadName={uploadName}
            uploadIndex={uploadIndex}
            uploadTotal={uploadTotal}
            uploadOverallProgress={uploadOverallProgress}
            uploadEta={uploadEta}
            cancelToken={cancelToken}
            API_URL={API_URL}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            />
      );
    }

    if (view === 'barcode') {
        return (
            <NewBarcodeView setView={setView} fields={cabinetFields} />
        );
    }

    if (view === 'search') {
        return (
            <SearchView
            query={query}
            setQuery={setQuery}
            handleSearch={handleSearch}
            clearSearch={clearSearch}
            results={results}
            hasSearched={hasSearched}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            parseFilename={parseFilename}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            API_URL={API_URL}
            onEmail={(filename) => { setEmailFilename(filename); setEmailModalOpen(true); }}
            />
        );
    }

    if (view === 'manage') {
        return (
            <ManageView
            fileList={fileList}
            handleDelete={handleDelete}
            parseFilename={parseFilename}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            API_URL={API_URL}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            onEmail={(filename) => { setEmailFilename(filename); setEmailModalOpen(true); }}
            />
        );
    }
    
    if (view === 'all') {
        return (
            <AllFilesView
            fileList={fileList}
            parseFilename={parseFilename}
            fieldLabels={cabinetFields}
            fieldKeys={fieldKeys}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSort={handleSort}
            API_URL={API_URL}
            selectedRow={selectedRow}
            setSelectedRow={setSelectedRow}
            onDelete={handleDelete}
            onEmail={(filename) => { setEmailFilename(filename); setEmailModalOpen(true); }}
            />
        );
    }

    if (view === 'admin-users') {
        return (
            <UsersView
              API_URL={API_URL}
            />
        );
    }
    if (view === 'admin-status') {
        return (
            <AdminStatusView
              API_URL={API_URL}
            />
        );
    }
    if (view === 'admin-cabinets') {
        return (
            <AdminCabinetsView
              API_URL={API_URL}
            />
        );
    }
  }; // render content

    const username = "John Doe";

    

    if (!auth.token) {
      return <LoginView API_URL={API_URL} onSuccess={handleLoginSuccess} />;
    }

    return (
        <div className="app-container">
            {/* Fixed Top Header */}
            <TopHeader
              customerName={customerName}
              username={(auth?.user && (`${auth.user.first_name || ''} ${auth.user.last_name || ''}`.trim() || auth.user.login)) || 'User'}
              ocrStatus={ocrStatus}
              theme={(uiTheme || 'light').toLowerCase()}
              onToggleTheme={async () => {
                try {
                  const current = (uiTheme || 'light').toLowerCase();
                  const nextTheme = current === 'dark' ? 'light' : 'dark';
                  // Optimistically apply theme for instant feedback
                  setUiTheme(nextTheme);
                  document.documentElement.setAttribute('data-theme', nextTheme);
                  try { localStorage.setItem('theme', nextTheme); } catch (_) {}
                  const res = await authFetch(`${API_URL}/me/theme`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ theme: nextTheme })
                  });
                  if (!res.ok) throw new Error('Failed to update theme');
                  setAuth(a => ({ ...a, user: { ...(a.user || {}), theme: nextTheme } }));
                } catch (e) {
                  console.error(e);
                }
              }}
              onLogout={() => {
                clearToken();
                setAuth({ token: '', user: null });
              }}
              onOpenCabinets={() => setCabinetsOpen(true)}
            />

            {searching && (
                <div className="upload-spinner-overlay">
                    <div className="upload-spinner-box">
                    <div className="spinner-large"></div>
                    <p>Searching, please wait...</p>
                    <button
                        onClick={() => {
                        if (searchCancelToken) {
                            searchCancelToken.cancel('User cancelled search');
                        }
                        }}
                        className="btn btn-delete"
                        style={{ marginTop: '10px' }}
                    >
                        Cancel Search
                    </button>
                    </div>
                </div>
            )}

            {/* Main layout (pushed down by 60px header) */}
            <div className="app-main">
                {/* Sidebar */}
                <SidebarMenu view={view} setView={setView} />

                {/* Right-side Work Area */}
                <div id="work" className="app-work">

                    {renderContent()}
                </div>
            </div>

            <EmailModal
              open={emailModalOpen}
              filename={emailFilename}
              onClose={() => { if (!emailBusy) setEmailModalOpen(false); }}
              busy={emailBusy}
              busyText={emailBusyText}
              onSend={async ({ to, cc, bcc, subject, message, pagesMode, pageRange }) => {
                try {
                  setEmailBusyText('Preparing email…');
                  setEmailBusy(true);
                  const body = {
                    to, cc, bcc, subject, message,
                    pagesMode: pagesMode === 'range' ? 'range' : 'all',
                    pageRange
                  };
                  setEmailBusyText('Sending email…');
                  const res = await authFetch(`${API_URL}/files/${encodeURIComponent(emailFilename)}/email`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                  });
                  if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.message || 'Failed to send email');
                  }
                  alert('Email sent');
                  setEmailModalOpen(false);
                } catch (e) {
                  alert(e.message || 'Failed to send email');
                } finally {
                  setEmailBusy(false);
                }
              }}
            />
            <CabinetsModal
              open={cabinetsOpen}
              API_URL={API_URL}
              onClose={() => setCabinetsOpen(false)}
              onSelect={async (cab) => {
                try {
                  const res = await authFetch(`${API_URL}/cabinets/${cab.id}/select`, { method: 'POST' });
                  if (!res.ok) {
                    const j = await res.json().catch(() => ({}));
                    throw new Error(j.message || 'Failed to switch cabinet');
                  }
                  const payload = await res.json().catch(() => ({}));
                  const active = payload?.active || { name: cab.name, fields: '' };
                  const nextName = active.name || cab.name;
                  setCustomerName(nextName);
                  try { localStorage.setItem('customerName', String(nextName)); } catch (_) {}
                  const raw = String(active?.fields || '').trim();
                  const labels = (raw ? raw.split(';') : []).map(s => s.trim()).filter(Boolean);
                  const usedLabels = labels.length ? labels : ['File #', 'Buyer Name', 'Seller Name'];
                  const keys = usedLabels.map(l => l.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, ''));
                  setCabinetFields(usedLabels);
                  setFieldKeys(keys);
                  setSortField(keys[0] || null);
                  // Reload files list to reflect the new cabinet
                  await loadFiles();
                } catch (e) {
                  alert(e.message || 'Failed to switch cabinet');
                } finally {
                  setCabinetsOpen(false);
                }
              }}
            />

            {/* Toast (non-blocking) */}
            {toast.open && (
              <div style={toastContainerStyle}>
                <div className="sp-toast" style={{
                  ...toastStyleBase,
                  backgroundColor: toastColors[toast.type] || toastColors.info,
                }}>
                  <span style={{ marginRight: 12 }}>{toast.message}</span>
                  <button
                    onClick={() => setToast(t => ({ ...t, open: false }))}
                    className="btn"
                    style={{ backgroundColor: 'rgba(0,0,0,0.2)', padding: '4px 8px' }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
        </div>
    );
} // app

// === Styles ===
const headerStyle = {
  width: '100vw',
  backgroundColor: '#0f2027',
  color: '#fff',
  padding: '10px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid #333',
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  boxSizing: 'border-box',
  height: '60px',
}
const input = {
  padding: '8px 12px',
  backgroundColor: '#1e1e1e',
  color: '#fff',
  border: '1px solid #333',
  borderRadius: 4,
};

const button = {
  marginLeft: 10,
  padding: '8px 14px',
  backgroundColor: '#1e88e5',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const deleteButton = {
  marginLeft: 10,
  padding: '6px 10px',
  backgroundColor: '#d32f2f',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const clearButton = {
  marginLeft: 10,
  padding: '6px 10px',
  backgroundColor: '#d32f2f',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
};

const table = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 20,
};

const th = {
  textAlign: 'left',
  padding: '12px 10px',
  backgroundColor: '#1e1e1e',
  color: '#90caf9',
  borderBottom: '2px solid #333',
  fontSize: '16px', // Increased font size
  fontWeight: 'bold'
};


const td = {
  padding: '10px',
  borderBottom: '1px solid #333',
  verticalAlign: 'top',
};

const rowEven = { backgroundColor: '#1a1a1a' };
const rowOdd = { backgroundColor: '#141414' };

const link = {
  color: '#64b5f6',
  textDecoration: 'none',
  marginRight: 10,
};

const viewButton = {
  padding: '6px 12px',
  backgroundColor: '#1565c0',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  textDecoration: 'none',
  display: 'inline-block',
  transition: 'background-color 0.2s',
};



export default App;

// Lightweight inline styles for toast
const toastContainerStyle = {
  position: 'fixed',
  right: 16,
  top: 70, // just below 60px header
  zIndex: 3000,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const toastStyleBase = {
  minWidth: 240,
  maxWidth: 420,
  padding: '10px 12px',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const toastColors = {
  success: '#2e7d32',
  info: '#1565c0',
  error: '#c62828',
};
